import { Color, Spacing } from '@persona-id/design-tokens';
import { XIcon } from '@persona-id/design-tokens/icons/24px';
import { useState } from 'react';
import styled from 'styled-components';

import Form from 'dashboard/layouts/FormLayout/Form';
import { ModalContainer } from 'dashboard/layouts/ModalLayout';
import { AccountType } from 'dashboard/models/account-type';
import { DatumValueType } from 'dashboard/models/graph';
import { PresetType, ServerPresetSummary } from 'dashboard/models/graph-backend';
import { DatumTypeSource } from 'dashboard/models/graph-datum-type';
import Button from 'design-system/components/Button';
import { Heading } from 'design-system/components/Text';
import { useFormSecurely } from 'lib/security/reactHookForm';

import BasicNodeInfo from './BasicNodeInfo';
import NodeFunctionSelector from './NodeFunctionSelector';

export interface NewGraphNodeFormData {
  datumType: DatumValueType | null;
  name: string;
  sources: DatumTypeSource[];
  presetType: PresetType;
}

const ButtonContainer = styled.div<{ withBorder: boolean }>`
  ${({ withBorder }) => withBorder && `padding-bottom: ${Spacing[5]};`}
  border-top: 1px solid ${Color.CoreSlate100};
  display: flex;
  justify-content: flex-end;
  padding: ${Spacing[5]};
`;

const NextButton = styled(Button)`
  margin-left: ${Spacing[2]};
`;

const FormContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledModalContainer = styled(ModalContainer)`
  padding-top: 0;
`;

const HeadingContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${Spacing[5]} ${Spacing[5]} ${Spacing[3]} ${Spacing[5]};
  border-bottom: 1px solid ${Color.CoreSlate100};
`;

interface FormCreateGraphNodeProps {
  onSubmit: (data: NewGraphNodeFormData) => Promise<void>;
  accountTypes: AccountType[];
  presetSummaries: ServerPresetSummary[];
  setIsModalOpen: (isOpen: boolean) => void;
}

enum CurrentSection {
  BasicNodeInfo = 'basic_node_info',
  NodeFunctionsSelector = 'node_functions_selector',
}

const FormCreateGraphNode = ({
  onSubmit,
  accountTypes,
  presetSummaries,
  setIsModalOpen,
}: FormCreateGraphNodeProps) => {
  const [currSection, setCurrSection] = useState<CurrentSection>(CurrentSection.BasicNodeInfo);
  const { setValue, control, register, watch, handleSubmit } =
    useFormSecurely<NewGraphNodeFormData>({
      defaultValues: {
        datumType: null,
        name: '',
        sources: [],
        presetType: presetSummaries?.[0]?.preset,
      },
    });

  const handleFormSubmit = (data: NewGraphNodeFormData) => {
    onSubmit(data);
    setIsModalOpen(false);
  };

  const datumType = watch('datumType');
  const name = watch('name');
  const sources = watch('sources');
  const presetType = watch('presetType');
  const isSubmission =
    datumType === DatumValueType.EmailSimilarity ||
    datumType === DatumValueType.ImageBackgroundSimilarity ||
    currSection === CurrentSection.NodeFunctionsSelector;
  const isCompleteSimilarity = datumType && name;
  const isCompleteNodeFunctionsBasicInfo = !datumType && name && sources.length > 0;
  return (
    <StyledModalContainer size={currSection === CurrentSection.NodeFunctionsSelector ? 'xl' : 'md'}>
      <HeadingContainer>
        <Heading size='sm'>
          {currSection === CurrentSection.NodeFunctionsSelector ? 'Select a Function' : 'New node'}
        </Heading>
        <XIcon id='graph__create-node' onClick={() => setIsModalOpen(false)} />
      </HeadingContainer>
      <Form id='form-create-graph-node'>
        <FormContent>
          {currSection === CurrentSection.BasicNodeInfo && (
            <BasicNodeInfo
              control={control}
              watch={watch}
              register={register}
              setValue={setValue}
              accountTypes={accountTypes}
              presetSummaries={presetSummaries}
            />
          )}
          {currSection === CurrentSection.NodeFunctionsSelector && (
            <NodeFunctionSelector control={control} presetSummaries={presetSummaries} />
          )}
          <ButtonContainer withBorder={currSection === CurrentSection.NodeFunctionsSelector}>
            <Button
              id='form-create-graph-node__btn-cancel'
              onClick={() => setIsModalOpen(false)}
              width='auto'
              variant='basic'
            >
              Cancel
            </Button>
            {isSubmission ? (
              <NextButton
                id='form-create-graph-node__btn-next'
                type='button'
                width='auto'
                onClick={handleSubmit(handleFormSubmit)}
                disabled={
                  !(datumType === DatumValueType.EmailSimilarity ||
                  datumType === DatumValueType.ImageBackgroundSimilarity
                    ? isCompleteSimilarity
                    : isCompleteNodeFunctionsBasicInfo && presetType)
                }
              >
                Next
              </NextButton>
            ) : (
              <NextButton
                id='form-create-graph-node__btn-next'
                type='button'
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setCurrSection(CurrentSection.NodeFunctionsSelector);
                }}
                width='auto'
                disabled={!isCompleteNodeFunctionsBasicInfo}
              >
                Next
              </NextButton>
            )}
          </ButtonContainer>
        </FormContent>
      </Form>
    </StyledModalContainer>
  );
};

export default FormCreateGraphNode;
